// src/pages/WatchVideo.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { useAuth } from '../components/AuthContext';
import Layout from '../components/Layout';
import Login from '../components/Login'
import './WatchVideo.css';

function WatchVideo() {
    const { videoId } = useParams();
    const navigate = useNavigate();
    const [video, setVideo] = useState(null);
    const [playingUrl, setPlayingUrl] = useState(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:5000/api';
    const playerRef = useRef(null);
    const [playerReady, setPlayerReady] = useState(false);
    const { currentUser, setAuthError } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [seekTimeInput, setSeekTimeInput] = useState('');

    const [allVideos, setAllVideos] = useState([]); // Store all videos for navigation
    const [currentVideoIndex, setCurrentVideoIndex] = useState(-1);
    const [prevVideoId, setPrevVideoId] = useState(null);
    const [nextVideoId, setNextVideoId] = useState(null);

    useEffect(() => {
        const fetchVideoDetailsAndNavData = async () => {
            if (currentUser) {
                try {
                    const idToken = await currentUser.getIdToken();
                    // Fetch all video data (needed for previous/next navigation)
                    const allVideosResponse = await axios.get(`${backendUrl}/all-data`, {
                        headers: { 'Authorization': `Bearer ${idToken}` }
                    });
                    setAllVideos(allVideosResponse.data);

                    // Find the current video and its index
                    const currentVideoData = allVideosResponse.data.find(v => v.id === videoId);
                    if (currentVideoData) {
                        setVideo(currentVideoData);
                        setPlayingUrl(currentVideoData.url); // Use URL from all-data

                        const currentIdx = allVideosResponse.data.findIndex(v => v.id === videoId);
                        setCurrentVideoIndex(currentIdx);

                        // Set previous/next video IDs
                        if (currentIdx > 0) {
                            setPrevVideoId(allVideosResponse.data[currentIdx - 1].id);
                        } else {
                            setPrevVideoId(null);
                        }
                        if (currentIdx < allVideosResponse.data.length - 1) {
                            setNextVideoId(allVideosResponse.data[currentIdx + 1].id);
                        } else {
                            setNextVideoId(null);
                        }
                    }

                } catch (error) {
                    console.error("Error fetching video details or navigation data:", error);
                    // Handle auth errors etc.
                }
            }
        };

        fetchVideoDetailsAndNavData();
    }, [videoId, currentUser, backendUrl]); // Re-fetch if videoId or user changes

    const handlePlayerReady = () => {
        setPlayerReady(true);
        playerRef.current.getInternalPlayer().play();
    };

    const handleJumpToQuestion = (questionText) => {
        if (video && video.questions && playerRef.current && playerReady) {
            const foundQuestion = video.questions.find(q => q.question_text === questionText);
            if (foundQuestion && typeof foundQuestion.question_start === 'number') {
                setSeekTimeInput(foundQuestion.question_start.toString()); // Update seekTimeInput
                playerRef.current.seekTo(foundQuestion.question_start);
                playerRef.current.getInternalPlayer().play();
            } else {
                console.warn(`question_start is not a valid number for question: ${questionText}`);
            }
        }
    };

    const handleJumpToAnswer = (questionText) => {
        if (video && video.questions && playerRef.current && playerReady) {
            const foundQuestion = video.questions.find(q => q.question_text === questionText);
            if (foundQuestion && typeof foundQuestion.answer_start === 'number') {
                setSeekTimeInput(foundQuestion.answer_start.toString()); // Update seekTimeInput
                playerRef.current.seekTo(foundQuestion.answer_start);
                playerRef.current.getInternalPlayer().play();
            } else {
                console.warn(`answer_start is not a valid number for question: ${questionText}`);
            }
        }
    };

    const navigateToVideo = (id) => {
        if (id) {
            navigate(`/watch/${id}`);
        }
    };

    const handleSeekInputChange = (event) => {
        setSeekTimeInput(event.target.value);
    };

    const handleSeek = () => {
        const seekTime = parseFloat(seekTimeInput);
        if (!isNaN(seekTime) && playerRef.current && playerReady) {
            playerRef.current.seekTo(seekTime);
            playerRef.current.getInternalPlayer().play();
        } else {
            console.warn("Invalid seek time input.");
        }
    };

    const showSeekInput = searchParams.get('debug') === 'true'; // Check for 'debug=true' in URL

    const handleSeekInputKeyPress = (event) => { // New function
        if (event.key === 'Enter') {
            handleSeek();
        }
    };

    if (!currentUser) {
        return (
            <div className="watch-video-container">
                <Login className="login-button" />
            </div>
        );
    }

    return (
        <Layout>
            {video && (
            <div className="watch-video-container">
                {/* Main content wrapper for video and questions */}
                <div className="video-and-questions-wrapper">
                    {/* Video player section */}
                    <div className="video-player-section">
                        <h1 className="watch-video-heading">{video.name}</h1>
                        {playingUrl && (
                            <ReactPlayer
                                ref={playerRef}
                                url={playingUrl}
                                controls
                                width="100%"
                                height="auto"
                                className="video-player"
                                onReady={handlePlayerReady}
                            />
                        )}
                        {showSeekInput && (
                            <div className="seek-input-container">
                                <input
                                    type="text"
                                    value={seekTimeInput}
                                    onChange={handleSeekInputChange}
                                    onKeyDown={handleSeekInputKeyPress} // Add onKeyDown event
                                    placeholder="Enter seconds"
                                    className="seek-input"
                                />
                                <button onClick={handleSeek} className="seek-button">
                                    Seek
                                </button>
                            </div>
                        )}
                        {/* Navigation Buttons for Previous/Next Video & Back to List */}
                        <div className="video-navigation-buttons">
                        <button onClick={() => navigateToVideo(prevVideoId)} className="nav-button" disabled={!prevVideoId}>
                            Previous
                        </button>
                        <button onClick={() => navigateToVideo(nextVideoId)} className="nav-button" disabled={!nextVideoId}>
                            Next
                        </button>
                        </div>
                    </div>
                    {/* Questions list section */}
                    <div className="questions-list-section">
                        {video.questions && (
                            <ul className="question-list">
                                {video.questions.map((question, index) => (
                                    <li key={index} className="question-item">
                                        {question.question_text}
                                        <div className="button-container">
                                            <button onClick={() => handleJumpToQuestion(question.question_text)} className="watch-button">
                                                Watch Question
                                            </button>
                                            <button onClick={() => handleJumpToAnswer(question.question_text)} className="watch-button">
                                                Watch Answer
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div> {/* End video-and-questions-wrapper */}
            </div>
            )}
        </Layout>
    );
}

export default WatchVideo;